'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { Button, Skeleton, Typography } from 'lighthouse';

import { getCartItemsQuantity } from '@/utilities/cart';
import { rialToToman } from '@/utilities/Financial';
import { withSeparator } from '@/utilities/withSeparator';

import { useCartStore } from '@/stores/cart/useCartStore';

import { checkoutCartPage } from '@/constants/routes';

import CartVendorGroup from './components/CartVendorGroup';

import { ShoppingCart } from 'iconsax-react';

const Cart = () => {
    const { cart, groupedCart, iaLoading } = useCartStore((state) => state);
    const router = useRouter();
    const cartItemsQuantity = getCartItemsQuantity(cart?.items);

    const [isDisplayCart, setIsDisplayCart] = useState(false);
    const [isViewCartLoading, setIsViewCartLoading] = useState(false);
    const cartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (cartRef.current && !cartRef.current.contains(e.target as Node)) {
                setIsDisplayCart(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsDisplayCart(false);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const handleOpenCart = () => {
        if (window.innerWidth >= 768) {
            setIsDisplayCart(!isDisplayCart);
        } else {
            setIsDisplayCart(false);
            router.push('/checkout/cart');
        }
    };

    const handleViewCart = () => {
        setIsViewCartLoading(true);
        router.push(checkoutCartPage);
    };

    return (
        <div className='relative' ref={cartRef}>
            <div className='group relative h-fit w-fit'>
                <Button aria-label='سبد خرید' disabled={iaLoading} className={'[&>span:first-child]:h-7'} onClick={handleOpenCart}>
                    <ShoppingCart
                        size='28'
                        color='currentColor'
                        variant={iaLoading ? 'Bulk' : 'Outline'}
                        className={`text-blackMediumEmphasis transition-all ease-out group-hover:text-primary ${iaLoading ? 'animate-pulse' : ''}`}
                    />

                    {!!cartItemsQuantity && (
                        <p
                            className='absolute -bottom-3 -right-1 flex h-6 w-6 items-center justify-center rounded-lg border-4 border-solid
                                border-white bg-primary text-xs text-white'>
                            {cartItemsQuantity}
                        </p>
                    )}
                </Button>
            </div>

            <div
                className={`absolute left-0 top-[calc(100%+1rem)] z-50 mt-2 w-96 overflow-hidden rounded-lg border border-solid border-gray-200
                    bg-white shadow-xl transition-all duration-300 ease-[cubic-bezier(.4,0,.2,1)] ${
                        isDisplayCart
                            ? 'visible translate-y-0 scale-100 opacity-100'
                            : 'pointer-events-none invisible -translate-y-2 scale-95 opacity-0'
                    }`}>
                <div className='!border-b !border-l-0 !border-r-0 !border-t-0 border-solid !border-gray-300 p-0 shadow-md'>
                    <div className='flex w-full items-center justify-center bg-gray-50 p-2'>
                        <Typography variant='body1' className='font-bold text-blackMediumEmphasis'>
                            سبد خرید
                        </Typography>
                    </div>
                </div>

                <div className='max-h-[422px] overflow-auto'>
                    {iaLoading ? (
                        <div className='space-y-2'>
                            {[1, 2, 3].map((item) => (
                                <div key={item} className='overflow-hidden rounded-lg border border-solid border-gray-100'>
                                    <div className='flex items-center gap-3 p-3'>
                                        <Skeleton variant='rounded' width={56} height={56} />
                                        <div className='flex-1 space-y-1'>
                                            <Skeleton width='80%' height={18} />
                                            <Skeleton width='55%' height={14} />
                                        </div>
                                    </div>
                                    <hr className='mb-0 opacity-10' />
                                    <div className='flex items-center justify-between bg-gray-50 p-2'>
                                        <Skeleton width={72} height={28} />
                                        <Skeleton width={96} height={20} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !!cart?.items.length && groupedCart?.items.length ? (
                        <div className='divide-y divide-gray-200'>
                            {groupedCart.items.map((store) => (
                                <CartVendorGroup
                                    key={store.provider}
                                    cartId={cart.cartId}
                                    provider={store.provider}
                                    storeName={store.storeName}
                                    fulfillmentGroups={store.fulfillmentGroups}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className='flex flex-col items-center justify-center gap-3 py-8 text-center'>
                            <ShoppingCart color='currentcolor' size='48' className='text-gray-300' />
                            <p className='text-sm font-semibold text-blackMediumEmphasis'>سبد خرید شما خالی است</p>
                            <Button href='/' size='small' variant='outlined' className='text-primary'>
                                مشاهده محصولات
                            </Button>
                        </div>
                    )}
                </div>

                {!iaLoading && !!cart?.items.length && (
                    <>
                        <hr className='m-0 opacity-20' />
                        <div className='flex h-16 w-full items-center justify-between border-t bg-gray-50 px-2'>
                            <Button loading={isViewCartLoading} onClick={handleViewCart} size='medium' className='text-white' variant='contained'>
                                مشاهده سبد خرید
                            </Button>

                            <div className='flex gap-8 rounded-lg px-4 py-1'>
                                <div className='flex items-center gap-2'>
                                    <span className='text-sm font-bold text-blackMediumEmphasis'>مبلغ کل:</span>
                                    <div className='flex items-baseline gap-1'>
                                        <span className='text-base font-bold text-primary'>{withSeparator(rialToToman(cart.finalTotal))}</span>
                                        <span className='text-xs font-bold text-blackHighEmphasis'>تومان</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Cart;
